import {
  BATTLE_ROYALE_INTERVAL_MAX_SECONDS,
  BATTLE_ROYALE_INTERVAL_MIN_SECONDS,
  BATTLE_ROYALE_ONE_PER_ROUND,
  BattleRoyaleEliminationRuleReadSchema,
  BattleRoyaleEliminationRuleSchema,
} from "../cede-arena/battle-royale";
import Decimal from "decimal.js";
import z from "zod";

// TRAILING / FROM_START: fixed threshold below peak / startingCollateralUSD. RISING: a floor on
// startingCollateralUSD that starts `initialFloorPercent` below it and rises `stepPercent` every
// `stepIntervalSeconds` from the epoch start.
export const DRAWDOWN_ELIMINATION_MODES = {
  TRAILING: "TRAILING",
  FROM_START: "FROM_START",
  RISING: "RISING",
} as const;
export type DrawdownEliminationMode = (typeof DRAWDOWN_ELIMINATION_MODES)[keyof typeof DRAWDOWN_ELIMINATION_MODES];

export const FixedDrawdownEliminationConfigSchema = z
  .object({
    mode: z.enum([DRAWDOWN_ELIMINATION_MODES.TRAILING, DRAWDOWN_ELIMINATION_MODES.FROM_START]),
    // % drop (0,100] from the reference that eliminates; 20 = 20% drawdown.
    thresholdPercent: z.number().positive().max(100),
  })
  .strict();
export type FixedDrawdownEliminationConfig = z.infer<typeof FixedDrawdownEliminationConfigSchema>;

export const RISING_DRAWDOWN_BOUNDS = {
  initialFloorPercent: { min: 1, max: 50 },
  stepPercent: { min: 0.1, max: 10 },
  stepIntervalSeconds: { min: 10 },
} as const;

export const RisingDrawdownEliminationConfigSchema = z
  .object({
    mode: z.literal(DRAWDOWN_ELIMINATION_MODES.RISING),
    initialFloorPercent: z
      .number()
      .min(RISING_DRAWDOWN_BOUNDS.initialFloorPercent.min)
      .max(RISING_DRAWDOWN_BOUNDS.initialFloorPercent.max),
    stepPercent: z.number().min(RISING_DRAWDOWN_BOUNDS.stepPercent.min).max(RISING_DRAWDOWN_BOUNDS.stepPercent.max),
    stepIntervalSeconds: z.number().int().min(RISING_DRAWDOWN_BOUNDS.stepIntervalSeconds.min),
  })
  .strict();
export type RisingDrawdownEliminationConfig = z.infer<typeof RisingDrawdownEliminationConfigSchema>;

export const DrawdownEliminationConfigSchema = z.discriminatedUnion("mode", [
  FixedDrawdownEliminationConfigSchema,
  RisingDrawdownEliminationConfigSchema,
]);
export type DrawdownEliminationConfig = z.infer<typeof DrawdownEliminationConfigSchema>;

export function isRisingDrawdown(
  config: DrawdownEliminationConfig | null | undefined,
): config is RisingDrawdownEliminationConfig {
  return config?.mode === DRAWDOWN_ELIMINATION_MODES.RISING;
}

export const RISING_DRAWDOWN_INTERVAL_EXCEEDS_DURATION_MESSAGE =
  "the floor step interval must be shorter than the competition duration";

/** A raise landing on the epoch end never applies, so the first one must land strictly before it. */
export function isRisingDrawdownIntervalWithinDuration(stepIntervalSeconds: number, durationSeconds: number): boolean {
  return stepIntervalSeconds < durationSeconds;
}

/** Full steps elapsed since the epoch start; 0 before it. */
export function risingDrawdownStep(
  config: RisingDrawdownEliminationConfig,
  epochStartMs: number,
  atMs: number,
): number {
  return Math.max(0, Math.floor((atMs - epochStartMs) / (config.stepIntervalSeconds * 1000)));
}

/** The step in force at `atMs`, frozen on the last step strictly before the epoch end: a step landing on the end never applies. */
export function risingDrawdownStepInForce(
  config: RisingDrawdownEliminationConfig,
  epochStartMs: number,
  epochEndMs: number,
  atMs: number,
): number {
  return risingDrawdownStep(config, epochStartMs, Math.min(atMs, epochEndMs - 1));
}

/** Floor as a signed % of starting capital at `step`: −initialFloorPercent + step × stepPercent. */
export function risingDrawdownFloorPercent(config: RisingDrawdownEliminationConfig, step: number): Decimal {
  return new Decimal(config.stepPercent).mul(step).minus(config.initialFloorPercent);
}

/** Equity at or below which a participant is eliminated: startingCollateral × (1 + floorPercent/100). */
export function risingDrawdownFloorEquity(
  config: RisingDrawdownEliminationConfig,
  step: number,
  startingCollateral: Decimal.Value,
): Decimal {
  return new Decimal(startingCollateral).mul(risingDrawdownFloorPercent(config, step).div(100).plus(1));
}

const battleRoyaleSecondsSchema = z
  .number()
  .int()
  .min(BATTLE_ROYALE_INTERVAL_MIN_SECONDS)
  .max(BATTLE_ROYALE_INTERVAL_MAX_SECONDS);

const BATTLE_ROYALE_CONFIG_TIMING = {
  eliminationIntervalSeconds: battleRoyaleSecondsSchema,
  // Warm-up before elimination 1 and the final 1v1's length; empty = the interval.
  firstEliminationSeconds: battleRoyaleSecondsSchema.nullish(),
  finalEliminationSeconds: battleRoyaleSecondsSchema.nullish(),
};

export const BattleRoyaleConfigSchema = z.strictObject({
  ...BATTLE_ROYALE_CONFIG_TIMING,
  // Configs written before elimination modes existed cut one player per elimination.
  elimination: BattleRoyaleEliminationRuleSchema.default(BATTLE_ROYALE_ONE_PER_ROUND),
  finalDuel: z.boolean().default(true),
});
/** For clients: strips keys a newer API added, at both levels, instead of throwing; an invalid value still throws. */
export const BattleRoyaleConfigReadSchema = z.object({
  ...BATTLE_ROYALE_CONFIG_TIMING,
  elimination: BattleRoyaleEliminationRuleReadSchema.default(BATTLE_ROYALE_ONE_PER_ROUND),
  finalDuel: z.boolean().default(true),
});
export type BattleRoyaleConfig = z.infer<typeof BattleRoyaleConfigSchema>;
export type BattleRoyaleConfigInput = z.input<typeof BattleRoyaleConfigSchema>;

/** How paper orders fill: at the mid price, at the touch (bid/ask), or by walking the order book. */
export const PAPER_PRICE_MODELS = { MID: "MID", TOUCH: "TOUCH", BOOK: "BOOK" } as const;
export type PaperPriceModel = (typeof PAPER_PRICE_MODELS)[keyof typeof PAPER_PRICE_MODELS];

// Unrefined shape for `.pick` callers; zod refuses `.pick` on a refined object.
export const PaperTradingConfigBaseSchema = z.object({
  startingCollateralUSD: z.number().positive(),
  /** bps, 0–10000 (0–100%). Omitted/null falls back to the module default (PAPER_MAKER_FEE_BPS/PAPER_TAKER_FEE_BPS), currently 0 — i.e. blank and 0 are both fee-free. Set a positive value to charge a fee. */
  makerFeeBps: z.number().int().nonnegative().max(10000).nullish(),
  takerFeeBps: z.number().int().nonnegative().max(10000).nullish(),
  maintenanceMarginRatio: z.number().positive().max(1).optional(),
  /** Optional drawdown-elimination policy. Omitted/null ⇒ feature off. */
  drawdown: DrawdownEliminationConfigSchema.nullish(),
  /** Optional Battle Royale elimination schedule. Omitted/null ⇒ classic format. */
  battleRoyale: BattleRoyaleConfigSchema.nullish(),
  priceModel: z.enum([PAPER_PRICE_MODELS.MID, PAPER_PRICE_MODELS.TOUCH, PAPER_PRICE_MODELS.BOOK]),
});

const rejectBattleRoyaleWithDrawdown = (c: { battleRoyale?: unknown; drawdown?: unknown }, ctx: z.RefinementCtx) => {
  if (c.battleRoyale != null && c.drawdown != null) {
    ctx.addIssue({ code: "custom", path: ["drawdown"], message: "Battle Royale cannot be combined with drawdown" });
  }
};

export const PaperTradingConfigSchema = PaperTradingConfigBaseSchema.superRefine(rejectBattleRoyaleWithDrawdown);
/** For responses: a Battle Royale key a newer API added is stripped instead of failing the whole campaign. */
export const PaperTradingConfigReadSchema = PaperTradingConfigBaseSchema.extend({
  battleRoyale: BattleRoyaleConfigReadSchema.nullish(),
}).superRefine(rejectBattleRoyaleWithDrawdown);

export type PaperTradingConfig = z.infer<typeof PaperTradingConfigSchema>;
